import { useEffect } from "react";
import { getSiteUrl } from "@/lib/siteUrl";

type SeoProps = {
  title?: string;
  description?: string;
  canonicalPath?: string; // "/path" (no domain)
  noindex?: boolean;
  ogImageUrl?: string; // absolute preferred
  jsonLd?: Record<string, any> | Record<string, any>[]; // Structured data
};

function upsertMetaByName(name: string, content: string) {
  let el = document.querySelector(`meta[name="${CSS.escape(name)}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertMetaByProperty(property: string, content: string) {
  let el = document.querySelector(`meta[property="${CSS.escape(property)}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let el = document.querySelector(`link[rel="canonical"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(jsonLd: SeoProps["jsonLd"]) {
  const id = "seo-jsonld";
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!jsonLd) {
    if (el?.parentNode) el.parentNode.removeChild(el);
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  const payload = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
  el.text = JSON.stringify(payload.length === 1 ? payload[0] : payload);
}

export function Seo({ title, description, canonicalPath, noindex, ogImageUrl, jsonLd }: SeoProps) {
  useEffect(() => {
    const site = getSiteUrl();
    const canonical = canonicalPath ? `${site}${canonicalPath.startsWith("/") ? "" : "/"}${canonicalPath}` : window.location.href;
    const finalTitle = title?.trim() ? title.trim() : "Halomora";
    const finalDesc =
      description?.trim() ? description.trim() : "Halomora — prenota servizi di pulizia e manutenzione vicino a te in pochi click.";
    const finalOg = ogImageUrl?.trim() ? ogImageUrl.trim() : `${site}/og.png`;

    document.title = finalTitle;
    upsertMetaByName("description", finalDesc);
    upsertCanonical(canonical);
    upsertJsonLd(jsonLd);

    // Robots
    if (noindex) upsertMetaByName("robots", "noindex, nofollow");
    else upsertMetaByName("robots", "index, follow");

    // OpenGraph / Twitter
    upsertMetaByProperty("og:title", finalTitle);
    upsertMetaByProperty("og:description", finalDesc);
    upsertMetaByProperty("og:type", "website");
    upsertMetaByProperty("og:url", canonical);
    upsertMetaByProperty("og:image", finalOg);

    upsertMetaByName("twitter:card", "summary_large_image");
    upsertMetaByName("twitter:image", finalOg);
  }, [title, description, canonicalPath, noindex, ogImageUrl, jsonLd]);

  return null;
}

